import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { useAccount, useBalance, useContractWrite, useWaitForTransaction, useChainId, useContractRead } from 'wagmi';
import { parseUnits, formatUnits } from 'viem';
import { VOTE_COSTS, getTxUrl, getContracts, isDynamicFeesEnabled, getLexiTokenAddress } from '../config/contracts';
import { useFactSubmissionFee, useDynamicFees } from '../hooks/useDynamicFees';
import { 
  ERC20ABI, 
  FactRegistryABI
} from '../abis';

interface FactSuggestionProps {
  preselectedOracle?: string;
}

const FactSuggestion: React.FC<FactSuggestionProps> = ({ preselectedOracle }) => {
  const { address, isConnected } = useAccount();
  const chainId = useChainId();
  const contracts = getContracts(chainId);
  const isDynamic = isDynamicFeesEnabled(chainId);
  const lexiTokenAddress = getLexiTokenAddress(chainId);
  
  // Dynamic fee hooks
  const { fee: factSubmissionFee, isLoading: feeLoading, error: feeError } = useFactSubmissionFee();
  const { minStake: minStakeConfig } = useDynamicFees();
  
  // Debug: Log fee information
  useEffect(() => {
    if (isDynamic && factSubmissionFee) {
      console.log('Fact Submission Fee from contract:', factSubmissionFee, '$LIVINGARCADE');
      if (feeError) {
        console.error('Error fetching fee:', feeError);
      }
    }
  }, [factSubmissionFee, feeError, isDynamic]);
  
  // Read minimum stake from contract (this is the source of truth)
  const { data: minStakeWei } = useContractRead({
    address: contracts.FACT_REGISTRY as `0x${string}`,
    abi: FactRegistryABI,
    functionName: 'MIN_STAKE_LEXI',
    query: {
      enabled: isDynamic && !!contracts.FACT_REGISTRY,
    },
  });
  
  // Convert minStake from wei to readable format, fallback to config if contract read fails
  // minStakeConfig is already in wei string format, so we need to parse it differently
  const minStake = minStakeWei 
    ? formatUnits(minStakeWei as bigint, 18)
    : minStakeConfig ? formatUnits(BigInt(minStakeConfig), 18) : '100';
  
  // Token balances
  const { data: tokenBalance } = useBalance({
    address,
    token: isDynamic ? lexiTokenAddress : contracts.VIBEPOOL_TOKEN,
  });

  const { data: ethBalance } = useBalance({
    address,
  });

  const { writeAsync: writeContractAsync, data: hashData, isLoading: isPending } = useContractWrite({
    address: isDynamic ? lexiTokenAddress : (contracts.VIBEPOOL_TOKEN as `0x${string}`),
    abi: ERC20ABI,
    functionName: 'transfer',
  });
  
  // Convert hash to proper format - handle both string and object responses
  const hash = hashData ? (typeof hashData === 'string' ? hashData : (hashData as any)?.hash || String(hashData)) : undefined;
  const { isSuccess: isConfirmed } = useWaitForTransaction({ hash: hash as `0x${string}` | undefined, enabled: !!hash });
  
  // Second hook for the fact proposal transaction
  const { writeAsync: writeContractAsync2, data: hash2Data } = useContractWrite({
    address: contracts.FACT_REGISTRY as `0x${string}`,
    abi: FactRegistryABI,
    functionName: 'proposeFact',
  });
  
  // Convert hash2 to proper format
  const hash2 = hash2Data ? (typeof hash2Data === 'string' ? hash2Data : (hash2Data as any)?.hash || String(hash2Data)) : undefined;
  const { isSuccess: isConfirmed2 } = useWaitForTransaction({ hash: hash2 as `0x${string}` | undefined, enabled: !!hash2 });

  // We'll skip the allowance check since the user already has 5 VIBE tokens approved

  const [selectedOracle, setSelectedOracle] = useState(preselectedOracle || '');
  const [factKey, setFactKey] = useState('');
  const [factValue, setFactValue] = useState('');
  const [source, setSource] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [step, setStep] = useState<'idle' | 'approving' | 'proposing' | 'success'>('idle');

  const availableOracles = useMemo(() => [
    { name: 'Geography', address: contracts.GEOGRAPHY_ORACLE, description: "Countries, capitals, landmarks, natural features" },
    { name: 'Philosophy', address: contracts.PHILOSOPHY_ORACLE, description: "Philosophical concepts and Living Arcade philosophy" },
    { name: 'Science', address: contracts.SCIENCE_ORACLE, description: "Scientific facts, discoveries, natural phenomena" },
    { name: 'History', address: contracts.HISTORY_ORACLE, description: "Historical events, cultures, civilizations" },
    { name: 'Arts', address: contracts.ARTS_ORACLE, description: "Literature, creative arts, cultural expressions" },
    { name: 'Technology', address: contracts.TECHNOLOGY_ORACLE, description: "Blockchain, Web3, technological innovations" },
  ], [contracts]);

  const handleOpenApprovalDialog = () => {
    if (!isConnected) {
      alert('Please connect your wallet first');
      return;
    }

    if (!selectedOracle || !factKey.trim() || !factValue.trim()) {
      alert('Please fill in all required fields');
      return;
    }

    // Check token balance for fact proposal fee
    const tokenBalanceAmount = tokenBalance ? parseFloat(tokenBalance.formatted) : 0;
    if (isDynamic) {
      const minStakeAmount = parseFloat(minStake);
      const feeAmount = parseFloat(factSubmissionFee);
      const requiredAmount = feeAmount + minStakeAmount;
      
      if (tokenBalanceAmount < requiredAmount) {
        alert(
          `Insufficient $LIVINGARCADE balance. You need at least ${requiredAmount.toFixed(2)} $LIVINGARCADE tokens to propose a fact.\n` +
          `(Fee: ${feeAmount.toFixed(2)} + Stake: ${minStakeAmount.toFixed(2)} = ${requiredAmount.toFixed(2)} $LIVINGARCADE)`
        );
        return;
      }
    } else {
      if (tokenBalanceAmount < parseFloat(VOTE_COSTS.PROPOSE_FACT)) {
        alert(`Insufficient VIBE balance. You need at least ${VOTE_COSTS.PROPOSE_FACT} VIBE tokens to propose a fact.`);
        return;
      }
    }

    // Check ETH balance for fact proposal stake (testnet only)
    if (!isDynamic) {
      const ethBalanceAmount = ethBalance ? parseFloat(ethBalance.formatted) : 0;
      if (ethBalanceAmount < 0.001) {
        alert('Insufficient ETH balance. You need at least 0.001 ETH to stake for fact proposals.');
        return;
      }
    }

    setApprovalDialogOpen(true);
  };

  const handleProposeFact = useCallback(async () => {
    try {
      console.log('🚀 === handleProposeFact called ===');
      console.log('Submitting fact proposal to FactRegistry...');
      console.log('FactRegistry address:', contracts.FACT_REGISTRY);
      console.log('Oracle address:', availableOracles.find(o => o.name === selectedOracle)?.address);
      console.log('Fact key:', factKey);
      console.log('Fact value:', factValue);
      console.log('Source:', source || '');
      console.log('Current step:', step);
      console.log('isDynamic:', isDynamic);
      console.log('minStake:', minStake);
      console.log('================================');
      
      // Step 2: Submit fact proposal to FactRegistry
      if (isDynamic) {
        // Mainnet: Use LEXI token stake
        const proposalTxHash = await (writeContractAsync2 as any)({
          address: contracts.FACT_REGISTRY,
          abi: FactRegistryABI,
          functionName: 'proposeFact',
          args: [
            availableOracles.find(o => o.name === selectedOracle)?.address,
            factKey,
            factValue,
            source || '',
            parseUnits(minStake, 18) // LEXI token stake
          ]
        });
        
        console.log('✅ Fact proposal transaction submitted successfully (mainnet)');
        console.log('📝 Proposal transaction hash:', proposalTxHash);
      } else {
        // Testnet: Use ETH stake
        const proposalTxHash = await (writeContractAsync2 as any)({
          address: contracts.FACT_REGISTRY,
          abi: FactRegistryABI,
          functionName: 'proposeFact',
          args: [
            availableOracles.find(o => o.name === selectedOracle)?.address,
            factKey,
            factValue,
            source || ''
          ],
          value: parseUnits('0.001', 18) // 0.001 ETH stake
        });
        
        console.log('✅ Fact proposal transaction submitted successfully (testnet)');
        console.log('📝 Proposal transaction hash:', proposalTxHash);
      }
      
      console.log('🎯 This should trigger a NEW wallet approval popup!');
      
    } catch (error: any) {
      console.error('❌ Proposal error:', error);
      alert(`Failed to submit fact proposal: ${error.message || 'Unknown error'}`);
      setStep('idle');
      setIsSubmitting(false);
    }
  }, [writeContractAsync2, contracts, selectedOracle, factKey, factValue, source, step, isDynamic, minStake, availableOracles]);

  const handleSubmitFact = async () => {
    console.log('=== handleSubmitFact called ===');
    console.log('address:', address);
    console.log('selectedOracle:', selectedOracle);
    console.log('isDynamic:', isDynamic);
    console.log('factSubmissionFee:', factSubmissionFee);
    console.log('===============================');
    
    if (!address || !selectedOracle) {
      console.log('❌ Missing address or selectedOracle');
      return;
    }

    setIsSubmitting(true);
    setStep('approving');

    try {
      // Check if user is admin (same as LEXI wallet) - skip fee transfer
      const isAdmin = address.toLowerCase() === contracts.LEXI_WALLET.toLowerCase();
      
      if (!isAdmin && parseFloat(factSubmissionFee) > 0) {
        console.log('🚀 Starting fee token transfer...');
        console.log('Token address:', isDynamic ? lexiTokenAddress : contracts.VIBEPOOL_TOKEN);
        console.log('LEXI wallet:', contracts.LEXI_WALLET);
        console.log('Amount:', factSubmissionFee);
        
        // Step 1: Transfer fee tokens to LEXI wallet
        const transferResult = await (writeContractAsync as any)({
          address: isDynamic ? lexiTokenAddress : contracts.VIBEPOOL_TOKEN,
          abi: ERC20ABI,
          functionName: 'transfer',
          args: [contracts.LEXI_WALLET, parseUnits(factSubmissionFee, 18)],
        });
        
        console.log('✅ Fee token transfer transaction submitted');
        console.log('Transfer result:', transferResult);
        
        // Extract hash from result if it's an object
        const txHash = typeof transferResult === 'string' ? transferResult : transferResult?.hash || transferResult;
        console.log('Transaction hash:', txHash);
        
        // Wait for the fee transfer to be confirmed before proposing fact
        // The useWaitForTransaction hook will handle the confirmation and trigger handleProposeFact
      } else {
        console.log('👑 Admin user detected or no fee required - skipping token transfer');
        // For admin users or when no fee is required, proceed directly to proposal
        setStep('proposing');
        handleProposeFact();
        return;
      }
      
    } catch (error: any) {
      console.error('❌ Transfer error:', error);
      alert(`Failed to transfer fee tokens: ${error.message || 'Unknown error'}`);
      setStep('idle');
      setIsSubmitting(false);
    }
  };

  // Handle approval transaction completion
  useEffect(() => {
    console.log('=== Approval Transaction Effect ===');
    console.log('isConfirmed (approval):', isConfirmed);
    console.log('step:', step);
    console.log('hash (approval):', hash);
    console.log('===============================');
    
    if (isConfirmed && step === 'approving') {
      console.log('✅ Approval transaction confirmed, proceeding to fact proposal...');
      setStep('proposing');
      // Now submit the fact proposal
      handleProposeFact();
    }
  }, [isConfirmed, step, hash, handleProposeFact]);

  // Handle proposal transaction completion
  useEffect(() => {
    console.log('=== Proposal Transaction Effect ===');
    console.log('isConfirmed2 (proposal):', isConfirmed2);
    console.log('step:', step);
    console.log('hash2 (proposal):', hash2);
    console.log('===============================');
    
    if (isConfirmed2 && step === 'proposing') {
      console.log('✅ Proposal transaction confirmed!');
      setStep('success');
      setSubmitted(true);
      setApprovalDialogOpen(false);
      
      // Reset form
      setFactKey('');
      setFactValue('');
      setSource('');
      setSelectedOracle('');
      setIsSubmitting(false);
    }
  }, [isConfirmed2, step, hash2]);


  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography 
          variant="h4" 
          component="h1" 
          className="retro-terminal retro-terminal-text"
          sx={{ 
            fontFamily: "'Courier Prime', 'Courier New', monospace",
            fontSize: '24px',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '2px',
            textShadow: '0 0 5px #00ff00',
            color: '#00ff00'
          }}
          gutterBottom
        >
          SUGGEST NEW FACTS
        </Typography>
        <Typography 
          variant="h6" 
          className="retro-terminal retro-terminal-text"
          sx={{ 
            fontFamily: "'Courier Prime', 'Courier New', monospace",
            fontSize: '14px',
            opacity: 0.8,
            textTransform: 'uppercase',
            letterSpacing: '1px',
            color: '#00ff00'
          }}
          gutterBottom
        >
          PROPOSE KNOWLEDGE FOR ORACLES
        </Typography>
      </Box>

      {!isConnected && (
        <Alert severity="warning" sx={{ mb: 4 }}>
          Connect your wallet to suggest and vote on facts.
        </Alert>
      )}


      <Grid container spacing={3}>
        {/* Suggest Fact Form */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 4 }}>
            <Typography variant="h5" gutterBottom>
              Submit a New Fact
            </Typography>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleOpenApprovalDialog();
              }}
            >
              <FormControl fullWidth sx={{ mb: 3 }} required disabled={!isConnected || isSubmitting}>
                <InputLabel>Select Oracle</InputLabel>
                <Select
                  value={selectedOracle}
                  label="Select Oracle"
                  onChange={(e) => setSelectedOracle(e.target.value as string)}
                >
                  {availableOracles.map((oracle) => (
                    <MenuItem key={oracle.name} value={oracle.name}>
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {oracle.name} Oracle
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {oracle.description}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="Fact Key"
                placeholder="e.g., capital_of_germany"
                value={factKey}
                onChange={(e) => setFactKey(e.target.value)}
                sx={{ mb: 3 }}
                required
                disabled={!isConnected || isSubmitting}
                helperText="Unique identifier for the fact (lowercase, underscores only)"
              />
              <TextField
                fullWidth
                label="Fact Value"
                placeholder="e.g., Berlin is the capital and largest city of Germany."
                value={factValue}
                onChange={(e) => setFactValue(e.target.value)}
                multiline
                rows={3}
                sx={{ mb: 3 }}
                required
                disabled={!isConnected || isSubmitting}
                helperText="The actual knowledge content"
              />
              <TextField
                fullWidth
                label="Source (Optional)"
                placeholder="e.g., Wikipedia, Academic Journal"
                value={source}
                onChange={(e) => setSource(e.target.value)}
                sx={{ mb: 3 }}
                disabled={!isConnected || isSubmitting}
                helperText="Where this fact can be verified"
              />
              
              {/* Fee Information */}
              <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Fee Information
                </Typography>
                {feeLoading && !factSubmissionFee ? (
                  <Box display="flex" alignItems="center" gap={1}>
                    <CircularProgress size={16} />
                    <Typography variant="body2" color="text.secondary">
                      Loading fees...
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {isDynamic ? (
                        <>
                          <strong>Submission Fee:</strong> {factSubmissionFee ? `${parseFloat(factSubmissionFee).toFixed(4)}` : 'Loading...'} $LIVINGARCADE tokens<br/>
                          <strong>Minimum Stake:</strong> {parseFloat(minStake).toFixed(2)} $LIVINGARCADE tokens<br/>
                          {feeError && (
                            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                              Warning: Could not fetch fee from contract. Using fallback values.
                            </Typography>
                          )}
                          <em>Fees are calculated dynamically based on $LIVINGARCADE token price</em>
                        </>
                      ) : (
                        <>
                          <strong>Submission Fee:</strong> {VOTE_COSTS.PROPOSE_FACT} VIBE tokens<br/>
                          <strong>Stake:</strong> 0.001 ETH<br/>
                          <em>Testnet fees for development and testing</em>
                        </>
                      )}
                    </Typography>
                  </Box>
                )}
              </Box>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                disabled={
                  !isConnected ||
                  isSubmitting ||
                  !selectedOracle ||
                  !factKey.trim() ||
                  !factValue.trim() ||
                  feeLoading
                }
                sx={{ height: 50 }}
              >
                {isSubmitting ? (
                  <CircularProgress size={24} />
                ) : feeLoading ? (
                  'Loading fees...'
                ) : (
                  'Submit Fact'
                )}
              </Button>
            </form>
            {submitted && (
              <Alert severity="success" sx={{ mt: 3 }}>
                Fact suggestion submitted! It will now appear in the pending list for community voting.
                {hash && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                    <a
                      href={getTxUrl(chainId, typeof hash === 'string' ? hash : String(hash || ''))}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: 'inherit' }}
                    >
                      View transaction ↗
                    </a>
                  </Typography>
                )}
              </Alert>
            )}
          </Paper>
        </Grid>

        {/* Info Panel */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 4 }}>
            <Typography variant="h5" gutterBottom>
              How It Works
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              <strong>1. Propose a Fact</strong>
              <br />
              Submit a new fact with a unique key and accurate value. Fees are calculated dynamically based on current $LIVINGARCADE token price using Chainlink price feeds. The fact will be submitted to the FactRegistry for community voting.
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              <strong>2. Community Review</strong>
              <br />
              Other users can review your proposed fact. Facts are stored on-chain immediately after approval.
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              <strong>3. Cost & Storage</strong>
              <br />
              Fees are calculated dynamically at the time of transaction based on the current $LIVINGARCADE/USD exchange rate. Once approved, your fact becomes a permanent part of LEXI's knowledge base, stored immutably on the Base blockchain.
            </Typography>

            <Alert severity="success" sx={{ mt: 3 }}>
              <Typography variant="body2">
                <strong>Fact Key Guidelines:</strong>
                <br />
                • Use lowercase letters only
                <br />
                • Separate words with underscores
                <br />
                • Be specific and descriptive
                <br />• Example: capital_of_germany
              </Typography>
            </Alert>

          </Paper>
        </Grid>
      </Grid>

      {/* Approval Dialog */}
      <Dialog open={approvalDialogOpen} onClose={() => !isSubmitting && setApprovalDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Fact Submission</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            You are about to submit a new fact to the <strong>{selectedOracle} Oracle</strong>:
          </Typography>
          <Paper sx={{ p: 2, bgcolor: 'background.default', mb: 2 }}>
            <Typography variant="body2">
              <strong>Key:</strong> {factKey}
            </Typography>
            <Typography variant="body2">
              <strong>Value:</strong> {factValue}
            </Typography>
            {source && (
              <Typography variant="body2">
                <strong>Source:</strong> {source}
              </Typography>
            )}
          </Paper>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Transaction Steps:</strong>
                <br />
                {isDynamic ? (
                  <>
                    1. Approve $LIVINGARCADE tokens for FactRegistry (fee + stake)
                    <br />
                    2. Submit the fact proposal to FactRegistry
                    <br />
                    <br />
                    <strong>Total Cost:</strong> {parseFloat(factSubmissionFee).toFixed(4)} $LIVINGARCADE fee + {parseFloat(minStake).toFixed(2)} $LIVINGARCADE stake + gas fees
                  </>
                ) : (
                  <>
                    1. Approve {VOTE_COSTS.PROPOSE_FACT} VIBE tokens for the LEXI wallet
                    <br />
                    2. Submit the fact proposal to FactRegistry
                    <br />
                    3. Pay 0.001 ETH stake (anti-spam protection)
                    <br />
                    <br />
                    <strong>Total Cost:</strong> {VOTE_COSTS.PROPOSE_FACT} VIBE tokens + 0.001 ETH stake + gas fees
                  </>
                )}
              </Typography>
            </Alert>
            
            {isConfirmed && step === 'approving' && (
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>✅ Step 1 Complete!</strong> VIBE token approval confirmed. 
                  <br />
                  Now proceeding to Step 2: Fact proposal to FactRegistry...
                  <br />
                  <strong>Please approve the second transaction in your wallet!</strong>
                </Typography>
              </Alert>
            )}
            
            {!isConfirmed && step === 'approving' && isPending && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>⏳ Step 1 in Progress...</strong> Waiting for VIBE token approval to be confirmed...
                </Typography>
              </Alert>
            )}
          {step !== 'idle' && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="primary">
                {step === 'approving' && '⏳ Step 1/2: Approving VIBE tokens...'}
                {step === 'proposing' && '⏳ Step 2/2: Submitting fact proposal to FactRegistry...'}
                {step === 'success' && '✅ Success! Fact proposed and available for voting.'}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApprovalDialogOpen(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitFact}
            disabled={isSubmitting || step === 'success'}
          >
            {isSubmitting ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                {step === 'approving' ? 'Approving...' : 'Proposing...'}
              </>
            ) : isDynamic ? (
              `Confirm (${parseFloat(factSubmissionFee).toFixed(4)} + ${parseFloat(minStake).toFixed(2)} $LIVINGARCADE)`
            ) : (
              `Confirm (${VOTE_COSTS.PROPOSE_FACT} VIBE + 0.001 ETH)`
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Transaction Status */}
      {hash && (
        <Alert
          severity={isConfirmed ? 'success' : 'info'}
          sx={{ position: 'fixed', bottom: 20, right: 20, minWidth: 300 }}
        >
          <Typography variant="body2">
            {isConfirmed ? (
              <>
                <strong>Fact Proposed!</strong>
                <br />
                <a href={getTxUrl(chainId, typeof hash === 'string' ? hash : String(hash || ''))} target="_blank" rel="noopener noreferrer">
                  View on BaseScan ↗
                </a>
              </>
            ) : (
              <>
                <strong>Processing...</strong>
                <br />
                Transaction: {hash ? (typeof hash === 'string' ? hash.slice(0, 10) : String(hash).slice(0, 10)) : 'Pending'}...
              </>
            )}
          </Typography>
        </Alert>
      )}
    </Container>
  );
};

export default FactSuggestion;
